import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSupplierNames } from '../../services/supplierService';
import { uploadProducts } from '../../services/uploadService';
import useUser from '../../auth/useUser';
import { useUploadTracking } from '../../context/UploadTrackingContext';
import AutocompleteInput from '../AutocompleteInput';
import AddNewSupplierModal from './AddNewSupplierModal';
import './UploadProductsModal.css';

const FILE_TYPES = ['PDF', 'CSV/Excel', 'PNG', 'JPG', 'DOC'];
const FILE_DESCRIPTIONS = ['Core Product List', 'Complementary Product List'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

/* --- Upload summary (reused when result modal reopens) --- */
export function UploadSummary({ data, fileDescription, referenceDate, supplierName, filename, onClose }) {
  const s = data.summary || {};
  const isCore = fileDescription === 'Core Product List';

  return (
    <div className="upload-summary">
      <div className="upload-summary-header">
        <span className="upload-summary-check">&#10003;</span> Upload Complete
      </div>
      <div className="upload-summary-meta">
        <div>{fileDescription} &middot; Reference Date {referenceDate}</div>
        <div>Supplier: {supplierName || data.supplier_name}</div>
        <div>File: {filename || data.original_filename}</div>
      </div>
      <ul className="upload-summary-counts">
        <li>Inserted: <strong>{s.inserted ?? 0}</strong> new products</li>
        {!isCore && <li>Updated: <strong>{s.updated ?? 0}</strong> products</li>}
        {!isCore && <li>Skipped: <strong>{s.skipped ?? 0}</strong> (already complete)</li>}
        {!isCore && <li>Archived: <strong>{s.archived ?? 0}</strong> to history</li>}
      </ul>
      {(s.needs_review_count > 0 || s.warnings_count > 0) && (
        <div className="upload-summary-warn">
          &#9888; {s.needs_review_count || s.warnings_count} warnings
        </div>
      )}
      <div className="upload-summary-actions">
        {data.action_history_id && (
          <a
            href={`/activity-log?id=${data.action_history_id}`}
            className="sup-btn"
            style={{ textDecoration: 'none' }}
          >
            View Activity Log
          </a>
        )}
        <button type="button" className="sup-btn primary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/* --- Main modal --- */
export default function UploadProductsModal({ onClose, onDone, onToast, preloadedFile }) {
  const { displayName } = useUser();
  const { startTracking } = useUploadTracking();
  const [fileType, setFileType] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [referenceDate, setReferenceDate] = useState('');
  const [supplierText, setSupplierText] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [file, setFile] = useState(preloadedFile || null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierNames, setSupplierNames] = useState([]);
  const [errors, setErrors] = useState({});
  const [shake, setShake] = useState(false);
  const [friendlyMessage, setFriendlyMessage] = useState(null); // { filename, userName }
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const fileRef = useRef(null);

  // Load supplier list for typeahead
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const names = await fetchSupplierNames();
        if (!cancelled) setSupplierNames(names);
      } catch { /* silent */ }
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/suppliers/list`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setSuppliers(data);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshSuppliers = useCallback(async () => {
    try {
      const names = await fetchSupplierNames();
      setSupplierNames(names);
    } catch {}
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/suppliers/list`);
      if (res.ok) { const data = await res.json(); setSuppliers(data); }
    } catch {}
  }, []);

  const supplierMap = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.supplier_name] = s.supplier_id; });
    return m;
  }, [suppliers]);

  const handleSupplierChange = useCallback((val) => {
    setSupplierText(val);
    if (supplierMap[val]) {
      setSelectedSupplier({ name: val, id: supplierMap[val] });
      setErrors(prev => ({ ...prev, supplier: null }));
    } else {
      setSelectedSupplier(null);
    }
  }, [supplierMap]);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      setErrors(prev => ({ ...prev, file: 'File exceeds 50 MB limit' }));
      setFile(null);
      return;
    }
    setFile(f);
    setErrors(prev => ({ ...prev, file: null }));
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const isValid = fileType && fileDescription && referenceDate && selectedSupplier && file;

  const handleUpload = async () => {
    const newErrors = {};
    if (!fileType) newErrors.fileType = 'Required';
    if (!fileDescription) newErrors.fileDescription = 'Required';
    if (!referenceDate) newErrors.referenceDate = 'Required';
    if (!selectedSupplier) newErrors.supplier = 'Required';
    if (!file) newErrors.file = 'Required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    try {
      const result = await uploadProducts(file, fileType, fileDescription, referenceDate, selectedSupplier.id, selectedSupplier.name, displayName);

      // Hand off polling to the context (survives navigation)
      startTracking({
        processId: result.process_id,
        filename: file.name,
        supplierName: selectedSupplier.name,
        fileDescription,
        referenceDate,
      });

      // Show friendly message then auto-close
      setFriendlyMessage({ filename: file.name, userName: displayName });
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      onToast?.({ type: 'error', msg });
    }
  };

  // Friendly "upload started" view
  if (friendlyMessage) {
    return (
      <div className="sup-modal-backdrop">
        <div className="sup-modal upload-modal" onClick={(e) => e.stopPropagation()}>
          <div className="sup-modal-body" style={{ textAlign: 'center', padding: '40px 30px' }}>
            <div style={{ fontSize: 40, color: 'var(--teal)', marginBottom: 12 }}>&#10003;</div>
            <h4 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>Upload Started</h4>
            <p style={{ fontSize: 14, color: 'var(--t2)', margin: 0, lineHeight: 1.7 }}>
              Dear {friendlyMessage.userName},
            </p>
            <p style={{ fontSize: 14, color: 'var(--t2)', margin: '4px 0 0', lineHeight: 1.7 }}>
              Your file &lsquo;<strong>{friendlyMessage.filename}</strong>&rsquo; is being processed.
              You&rsquo;ll be notified once it&rsquo;s complete.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">Upload Supplier Products</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>x</button>
        </div>

        <div className="sup-modal-body">
          {/* File Type */}
          <div className="upload-field">
            <label>File Type <span className="upload-req">*</span></label>
            <select
              className="upload-select"
              value={fileType}
              onChange={(e) => { setFileType(e.target.value); setErrors(prev => ({ ...prev, fileType: null })); }}
            >
              <option value="" disabled>Select file type...</option>
              {FILE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.fileType && <div className="upload-err">{errors.fileType}</div>}
          </div>

          {/* File Description */}
          <div className="upload-field">
            <label>File Description <span className="upload-req">*</span></label>
            <select
              className="upload-select"
              value={fileDescription}
              onChange={(e) => { setFileDescription(e.target.value); setErrors(prev => ({ ...prev, fileDescription: null })); }}
            >
              <option value="" disabled>Select description...</option>
              {FILE_DESCRIPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {errors.fileDescription && <div className="upload-err">{errors.fileDescription}</div>}
          </div>

          {/* Reference Date */}
          <div className="upload-field">
            <label>Reference Date <span className="upload-req">*</span></label>
            <input
              type="date"
              className="upload-select"
              value={referenceDate}
              min="2000-01-01"
              max={todayStr}
              onChange={(e) => { setReferenceDate(e.target.value); setErrors(prev => ({ ...prev, referenceDate: null })); }}
            />
            {errors.referenceDate && <div className="upload-err">{errors.referenceDate}</div>}
          </div>

          {/* Supplier */}
          <div className="upload-field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Supplier <span className="upload-req">*</span>
              <button
                type="button"
                onClick={() => setShowAddSupplier(true)}
                style={{ background: 'none', border: '1px solid var(--teal)', color: 'var(--teal)', borderRadius: 4, padding: '1px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
              >+ New Supplier</button>
            </label>
            <AutocompleteInput
              value={supplierText}
              onChange={handleSupplierChange}
              suggestions={supplierNames}
              placeholder="Start typing supplier name..."
              maxItems={8}
            />
            {errors.supplier && <div className="upload-err">{errors.supplier}</div>}
          </div>

          {/* File Upload */}
          <div className="upload-field">
            <label>Upload File <span className="upload-req">*</span></label>
            <div className="upload-file-row">
              <button type="button" className="upload-file-btn" onClick={() => fileRef.current?.click()}>
                Choose File
              </button>
              <span className="upload-file-name">{file ? file.name : 'No file chosen'}</span>
              <input
                ref={fileRef}
                type="file"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
            {errors.file && <div className="upload-err">{errors.file}</div>}
          </div>
        </div>

        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Cancel
          </button>
          <button
            type="button"
            className={`sup-btn primary${shake ? ' upload-shake' : ''}`}
            onClick={handleUpload}
            disabled={!isValid}
          >
            <i className="bi bi-upload me-1"></i> Upload
          </button>
        </div>
      </div>

      {showAddSupplier && (
        <AddNewSupplierModal
          onClose={() => setShowAddSupplier(false)}
          onCreated={async (newSup) => {
            setShowAddSupplier(false);
            await refreshSuppliers();
            setSupplierText(newSup.supplier_name);
            setSelectedSupplier({ name: newSup.supplier_name, id: newSup.supplier_id });
            setErrors(prev => ({ ...prev, supplier: null }));
          }}
        />
      )}
    </div>
  );
}
